import { useEffect, useRef } from "react";
import { gsap, prefersReducedMotion } from "@/lib/gsapSetup";
import { footer, brand } from "@/data/siteContent";
import { scrollToSection } from "@/hooks/useLenis";

export default function Footer() {
  const ref = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const ctx = gsap.context(() => {
      gsap.from(ref.current.querySelector("[data-bigword]"), {
        yPercent: 40,
        opacity: 0,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: { trigger: ref.current, start: "top 90%" },
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  const handleNav = (e, href) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      scrollToSection(href);
    }
  };

  return (
    <footer ref={ref} className="relative overflow-hidden bg-black pt-20 md:pt-24" data-testid="footer">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 gap-12 border-b border-white/10 pb-16 md:grid-cols-12">
          <div className="md:col-span-6">
            <p
              className="font-accent text-2xl italic text-gold/90"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {footer.invitation}
            </p>
            <p className="mt-6 max-w-sm text-sm text-white/60">{brand.tagline}</p>
          </div>

          {footer.columns.map((col) => (
            <div key={col.title} className="md:col-span-3">
              <h4 className="mb-5 text-xs uppercase tracking-[0.2em] text-white/55">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      onClick={(e) => handleNav(e, l.href)}
                      target={l.href.startsWith("http") ? "_blank" : undefined}
                      rel={l.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="text-sm text-white/70 transition-colors hover:text-gold"
                      data-testid={`footer-link-${l.label.toLowerCase()}`}
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col items-start justify-between gap-4 py-8 md:flex-row md:items-center">
          <span className="text-xs text-white/55">{brand.copyright}</span>
          <span className="text-xs text-white/55">{brand.established}</span>
        </div>
      </div>

      <div className="overflow-hidden px-4">
        <h2
          data-bigword
          className="select-none whitespace-nowrap text-center font-display text-[22vw] leading-[0.8] tracking-tighter text-white/[0.06] md:text-[20vw]"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {footer.bigWord}
        </h2>
      </div>
    </footer>
  );
}
